import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { updateUser } from '@/api/user'
import { Button } from '@/components/Button'
import { CheckIcon } from '@/components/icons/Check'
import { PhoneNumber } from '@/components/PhoneNumber'
import { useConfig } from '@/hooks/useConfig'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useUser } from '@/hooks/useUser'
import { useUserForm } from '@/hooks/useUserForm'
import { CountryCode } from '@/types/components'
import { FEATURE } from '@/types/config'

const HIDE_POPUP_TIMEOUT = 6_000 // 6s

enum SUBMIT_STATUS {
  SUCCESS = 'success',
  ERROR = 'error',
  SUBMITTING = 'submitting'
}

export const PhoneNumberPopup = () => {
  const { t } = useTranslation()
  const { config } = useConfig()
  const { user, updateUser: updateUserObj } = useUser()
  const { getFieldError } = useFormErrors()
  const { getUserDataById } = useUserForm()
  const phone = getUserDataById('phone') as string

  const [showPopup, setShowPopup] = useState(false)
  const [hidePopup, setHidePopup] = useState(false)
  const [showInput, setShowInput] = useState(false)

  const [submitStatus, setSubmitStatus] = useState<SUBMIT_STATUS | null>(null)

  const handleSubmit = async () => {
    if (getFieldError('phone') || submitStatus === SUBMIT_STATUS.SUBMITTING) return

    setSubmitStatus(SUBMIT_STATUS.SUBMITTING)

    try {
      const updatedUser = await updateUser({
        user: {
          ...user?.data,
          phone
        }
      })

      updateUserObj(updatedUser)
      setSubmitStatus(SUBMIT_STATUS.SUCCESS)

      setTimeout(() => {
        setHidePopup(true)
        setTimeout(() => {
          // This is needed in order to remove the component from the DOM after the animation is finished
          setShowPopup(false)
        }, 300)
      }, HIDE_POPUP_TIMEOUT)
    } catch (e) {
      console.error(e)
      setSubmitStatus(SUBMIT_STATUS.ERROR)
    }
  }

  useEffect(() => {
    if (user?.data.phone || !config?.features?.includes(FEATURE.ASK_PHONE_FOR_REMINDER)) return

    setShowPopup(true)
  }, [])

  if (!showPopup) return null

  return (
    <div
      className={`absolute bottom-0 left-0 flex w-full flex-col items-center gap-4 rounded-t-xl bg-white p-4 shadow-strong ${
        hidePopup ? 'animate-slide-down' : 'animate-slide-up'
      }`}
    >
      {submitStatus === SUBMIT_STATUS.SUCCESS ? (
        <>
          <p className="mt-4 text-center font-bold">{t('sms_reminder.confirmation')}</p>
          <CheckIcon />
        </>
      ) : showInput ? (
        <>
          <PhoneNumber
            id="phone"
            required
            tabIndex={0}
            defaultCountry={user?.data.country?.code?.toUpperCase() as CountryCode}
            value={phone}
            textKey="inputs.phone.label"
          />

          <Button customClassName="bg-success text-white" custom onClick={handleSubmit} loading={submitStatus === SUBMIT_STATUS.SUBMITTING}>
            {t('button.submit')}
          </Button>
        </>
      ) : (
        <Button
          customClassName="bg-success text-white"
          custom
          onClick={() => {
            setShowInput(true)

            // Focus on phone input
            setTimeout(() => {
              const input = document.getElementById('phone')
              input?.focus()
            }, 100)
          }}
        >
          {t('button.sms_reminder')}
        </Button>
      )}
    </div>
  )
}
